import { useGetAllBrands } from "@/api/queries";
import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";

export const Brands = () => {
  const { data: brandsData, isFetching } = useGetAllBrands();
  const brands = brandsData?.data || [];
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(0);
  const [showHint, setShowHint] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  const brandsPerPage = 5;
  const totalPages = Math.ceil(brands.length / brandsPerPage);
  const startIndex = currentPage * brandsPerPage;
  const visibleBrands = brands.slice(startIndex, startIndex + brandsPerPage);

  const goToNext = () => setCurrentPage((p) => (p + 1) % totalPages);
  const goToPrev = () => setCurrentPage((p) => (p - 1 + totalPages) % totalPages);

  const handleBrandClick = (brandId: string) => navigate(`/shop?brand=${brandId}`);

  // Hide hint after first scroll
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const onScroll = () => {
      if (scrollContainer.scrollLeft > 20) setShowHint(false);
    };

    scrollContainer.addEventListener("scroll", onScroll);
    return () => scrollContainer.removeEventListener("scroll", onScroll);
  }, []);

  if (isFetching) {
    return (
      <div className='w-full py-12 px-4'>
        <h2 className='text-3xl font-bold text-center mb-8'>Shop Brands You Love</h2>
        <div className='flex gap-4 overflow-hidden'>
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className='h-32 flex-1 min-w-[150px] rounded-xl' />
          ))}
        </div>
      </div>
    );
  }

  if (!brands.length) return null;

  return (
    <section className='py-4 px-4 md:px-8 mb-4 relative'>
      <div className='container mx-auto'>
        <h2 className='text-2xl font-bold text-gray-800 '>Shop Brands You Love</h2>

        <div className='relative max-w-7xl mx-auto'>
          {/* Desktop Pagination Controls */}
          {totalPages > 1 && (
            <>
              <Button
                variant='outline'
                size='icon'
                className='hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 rounded-full shadow-lg hover:scale-110 transition-transform'
                onClick={goToPrev}
              >
                <ChevronLeft className='h-5 w-5' />
              </Button>
              <Button
                variant='outline'
                size='icon'
                className='hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 rounded-full shadow-lg hover:scale-110 transition-transform'
                onClick={goToNext}
              >
                <ChevronRight className='h-5 w-5' />
              </Button>
            </>
          )}

          {/* Scrollable Brand Container (mobile) */}
          <div className='pt-6 md:px-8 relative'>
            <div
              ref={scrollRef}
              className='flex gap-4 overflow-x-auto md:overflow-hidden scrollbar-hide md:flex-nowrap scroll-smooth'
            >
              {(window.innerWidth < 768 ? brands : visibleBrands).map((brand) => (
                <div
                  key={brand.id}
                  onClick={() => handleBrandClick(brand.id)}
                  className='flex-none w-[150px] md:flex-1 h-32 bg-white rounded-xl border-2 border-gray-100 hover:border-primary transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer flex items-center justify-center p-4'
                >
                  {brand.logo ? (
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className='max-h-full max-w-full object-contain'
                    />
                  ) : (
                    <span className='font-semibold text-gray-700 text-center'>{brand.name}</span>
                  )}
                </div>
              ))}
            </div>

            {/* Scroll Gradient Overlays */}
            <div className='absolute top-0 left-0 h-full w-10 bg-gradient-to-r from-white to-transparent pointer-events-none md:hidden' />
            <div className='absolute top-0 right-0 h-full w-10 bg-gradient-to-l from-white to-transparent pointer-events-none md:hidden' />

            {/* Swipe Hint (mobile only) */}
            {showHint && (
              <div className='absolute bottom-0 right-4 text-sm text-gray-500 animate-pulse md:hidden'>
                ← Swipe to see more
              </div>
            )}
          </div>

          {/* Pagination Dots (desktop only) */}
          {totalPages > 1 && (
            <div className='hidden md:flex justify-center gap-2 mt-6'>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === currentPage ? "w-8 bg-primary" : "w-2 bg-gray-300 hover:bg-gray-400"
                  }`}
                  aria-label={`Go to page ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
